import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { isEqual } from 'lodash';
import { AccountService, Language, PartialLabelData, SMSTemplatesSettingsUpdateData } from '../../api';
import { SMS_TEMPLATE_TYPES, SMSTemplateType } from '../types/SMSTemplateTypes';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';
import { useLanguage } from '../../common/GeneralUtils';
import { getOrderPage } from '../../common/AccountUtils';
import { calculateDirection } from '../../emailTemplates/templateEditor/components/EmailSubjectEditor';
import { FormTextField } from '../../experienceSettings/formComponents/FormTextField';

type EditSMSTemplatesProps = {
  orderPageIdOverride?: string;
};

const TEMPLATE_VARIABLES = (t: any) => [
  { key: '{firstName}', descriptionKey: t('smsTemplates.variables.customer_name') },
  { key: '{orderDate}', descriptionKey: t('smsTemplates.variables.order_date') },
  { key: '{date}', descriptionKey: t('smsTemplates.variables.order_date') },
  { key: '{orderTime}', descriptionKey: t('smsTemplates.variables.order_time') },
  { key: '{accountName}', descriptionKey: t('smsTemplates.variables.business_name') },
  { key: '{orderNumber}', descriptionKey: t('smsTemplates.variables.order_number') },
  { key: '{activity}', descriptionKey: t('smsTemplates.variables.activity') },
  { key: '{location}', descriptionKey: t('smsTemplates.variables.order_location') },
];

export const EditSMSTemplates = ({ orderPageIdOverride }: EditSMSTemplatesProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { readOnly } = UseAuthPermissions();
  const { account } = useGetAccountAuthenticated({});
  const currentLanguage = useLanguage();
  const { t } = useTranslation();
  const isOverrideTemplates = !!orderPageIdOverride;

  const [selectedTemplate, setSelectedTemplate] = useState<SMSTemplateType>(SMSTemplateType.CONFIRMATION);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    account.customer_facing_widgets_languages[0] || currentLanguage,
  );
  const [isUpdatedSnackOpen, setIsUpdatedSnackOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDisabled = readOnly || isSubmitting;

  // Get order page if we have an override
  const orderPage = orderPageIdOverride ? getOrderPage(account, orderPageIdOverride) : null;

  // Use order page templates if available, otherwise fall back to account templates
  const getTemplateValue = (templateType: keyof SMSTemplatesSettingsUpdateData) => {
    if (orderPage?.sms_communication_settings?.[templateType]) {
      return orderPage.sms_communication_settings[templateType];
    }
    return (
      // @ts-ignore
      account?.sms_communication_settings?.[templateType] || {
        enabled: false,
        template: {},
        ...(templateType === SMSTemplateType.REMINDER && { pre_workshop_days: 1 }),
      }
    );
  };

  const initialValues: SMSTemplatesSettingsUpdateData = {
    confirmation: getTemplateValue('confirmation'),
    reminder: getTemplateValue('reminder'),
    order_page_id_override: orderPageIdOverride,
  };

  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateTemplate = (template: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check for unclosed template variables
    const openBraces = (template.match(/{/g) || []).length;
    const closeBraces = (template.match(/}/g) || []).length;
    if (openBraces !== closeBraces) {
      errors.push(t('smsTemplates.errors.unclosedVariables'));
    }

    // Check for invalid variables
    const variablePattern = /{([^}]+)}/g;
    const matches = template.match(variablePattern);
    if (matches) {
      const validVariables = TEMPLATE_VARIABLES(t).map((v) => v.key);
      const invalidVariables = matches.filter((match) => !validVariables.includes(match));
      if (invalidVariables.length > 0) {
        errors.push(t('smsTemplates.errors.invalidVariables', { variables: invalidVariables.join(', ') }));
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const onSubmit = async (values: SMSTemplatesSettingsUpdateData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate all templates before submitting
      const validationErrors: string[] = [];

      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'order_page_id_override' && value && typeof value === 'object') {
          const templateData = (value as any)?.template;
          if (templateData) {
            Object.entries(templateData).forEach(([_lang, template]) => {
              if (template) {
                const validation = validateTemplate(template as string);
                if (!validation.isValid) {
                  validationErrors.push(`${key} (${_lang}): ${validation.errors.join(', ')}`);
                }
              }
            });
          }
        }
      });

      if (validationErrors.length > 0) {
        setSubmitError(t('smsTemplates.errors.validationFailed', { errors: validationErrors.join('\n') }));
        return;
      }

      await AccountService.updateSmsTemplatesSettings(values);
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error('Error saving SMS templates:', error);
      setSubmitError(t('smsTemplates.errors.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateTypeChange = (_: React.MouseEvent<HTMLElement>, newTemplate: SMSTemplateType) => {
    if (!newTemplate) {
      return;
    }
    setSelectedTemplate(newTemplate);
  };

  const handleLanguageChange = (_: React.MouseEvent<HTMLElement>, newLanguage: Language) => {
    if (!newLanguage) {
      return;
    }
    setSelectedLanguage(newLanguage);
  };

  const calculateSMSLength = (template: string) => {
    if (!template) {
      return 0;
    }
    // Replace variables with sample data to get accurate character count
    const processedTemplate = template
      .replace(/{firstName}/g, 'John')
      .replace(/{orderDate}/g, '2024-01-15')
      .replace(/{date}/g, '2024-01-15')
      .replace(/{orderTime}/g, '14:30')
      .replace(/{accountName}/g, 'My Business')
      .replace(/{orderNumber}/g, '1234')
      .replace(/{activity}/g, 'Pottery Workshop')
      .replace(/{location}/g, 'Studio Downtown');
    console.log(processedTemplate);
    return processedTemplate.length;
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        confirmation: Yup.object().test('character-limit', (value: any) => {
          if (!value?.template) return true;

          for (const [_lang, template] of Object.entries(value.template)) {
            if (template && calculateSMSLength(template as string) > 130) {
              return false;
            }
          }
          return true;
        }),
        reminder: Yup.object()
          .test('character-limit', (value: any) => {
            if (!value?.template) return true;

            for (const [_lang, template] of Object.entries(value.template)) {
              if (template && calculateSMSLength(template as string) > 130) {
                return false;
              }
            }
            return true;
          })
          .test('pre-workshop-days', (value: any) => {
            if (!value?.enabled) return true;
            const days = value?.pre_workshop_days;
            return days >= 1 && days <= 30;
          }),
      }),
    [],
  );

  const isTemplateInherited = (templateType: keyof SMSTemplatesSettingsUpdateData) => {
    if (!orderPageIdOverride) {
      return false;
    }
    return !orderPage?.sms_communication_settings?.[templateType];
  };
  const textDirection = calculateDirection(selectedLanguage, currentLanguage);

  const renderCharacterCount = (values: any) => {
    const currentValue =
      values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData]?.template?.[selectedLanguage] || '';
    const charCount = calculateSMSLength(currentValue);
    const isOverLimit = charCount > 130;

    return (
      <Typography
        variant="caption"
        color={isOverLimit ? 'error' : 'text.secondary'}
        sx={{ mt: 0.5, alignSelf: 'flex-start', width: '100%' }}>
        <Typography variant="caption">{`${charCount}/130 ${t('smsTemplates.characters')}`}</Typography>
      </Typography>
    );
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, handleSubmit, setFieldValue, errors, touched }) => (
        <Form onSubmit={handleSubmit}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              height: isSmallScreen ? 'calc(100vh - 100px)' : '100vh',
              overflowY: 'auto',
            }}>
            <Grid container p={5} flexDirection="column">
              <Snackbar
                open={isUpdatedSnackOpen}
                onClose={() => setIsUpdatedSnackOpen(false)}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert
                  sx={{ width: '100%' }}
                  onClose={() => setIsUpdatedSnackOpen(false)}
                  severity="success"
                  variant="filled">
                  {t('editOrderPage.orderPageTypeSuccess')}
                </Alert>
              </Snackbar>

              {!isOverrideTemplates && (
                <Grid container justifyContent="center" mb={2} mt={-5}>
                  <Typography variant="h4">{t('smsTemplates.title')}</Typography>
                </Grid>
              )}

              <Grid container justifyContent="center">
                <ToggleButtonGroup
                  size="large"
                  value={selectedTemplate}
                  exclusive
                  onChange={handleTemplateTypeChange}
                  aria-label="template type selection">
                  {SMS_TEMPLATE_TYPES.map((type) => (
                    <ToggleButton key={type} value={type} aria-label={type}>
                      <Typography>{t(`smsTemplates.templateTypes.${type}`)}</Typography>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Box
                sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%', justifyContent: 'center' }}
                mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        (values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData] as any)?.enabled || false
                      }
                      onChange={(e) => {
                        const currentSettings =
                          (values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData] as any) || {};
                        void setFieldValue(selectedTemplate, {
                          ...currentSettings,
                          enabled: e.target.checked,
                        });
                      }}
                      disabled={isDisabled}
                    />
                  }
                  label={
                    selectedTemplate === SMSTemplateType.CONFIRMATION
                      ? t('smsTemplates.confirmationEnabled')
                      : t('smsTemplates.reminderEnabled')
                  }
                />
              </Box>
              {selectedTemplate === SMSTemplateType.REMINDER && (values.reminder as any)?.enabled && (
                <Grid container justifyContent={'center'} mb={2}>
                  <Grid item>
                    <FormTextField
                      type="number"
                      fieldName={'reminder.pre_workshop_days'}
                      label={t('smsTemplates.reminderDays')}
                      disabled={isDisabled}
                      min={0}
                      max={30}
                      width={200}
                      isFieldUpdate
                    />
                  </Grid>
                </Grid>
              )}
              {account.customer_facing_widgets_languages.length > 1 && (
                <Grid container mt={2} justifyContent="center" flexDirection="column" alignItems="center" gap={1}>
                  <Grid mb={3}>
                    <Divider style={{ width: '300px' }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="h5">{t('smsTemplates.language')}</Typography>
                  </Grid>
                  <ToggleButtonGroup
                    value={selectedLanguage}
                    exclusive
                    onChange={handleLanguageChange}
                    aria-label="language selection">
                    {account.customer_facing_widgets_languages.map((lang: Language) => (
                      <ToggleButton key={lang} value={lang} aria-label={lang}>
                        <Typography>{t(`smsTemplates.languages.${lang}`)}</Typography>
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>
              )}

              <Grid container justifyContent="center">
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  sx={{ maxWidth: isSmallScreen ? '100%' : '800px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                    <Typography variant="h6">{t('smsTemplates.templateContent')}</Typography>
                    {isTemplateInherited(selectedTemplate as keyof SMSTemplatesSettingsUpdateData) && (
                      <Chip
                        label={t('smsTemplates.inheritedFromAccount')}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    )}
                  </Box>

                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder={t('smsTemplates.placeholders.enterTemplate', {
                      templateType: t(`smsTemplates.templateTypes.${selectedTemplate}`),
                      language: t(`smsTemplates.languages.${selectedLanguage}`),
                    })}
                    value={
                      (values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData] as any)?.template?.[
                        selectedLanguage
                      ] || ''
                    }
                    onChange={(e) => {
                      const currentSettings =
                        (values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData] as any) || {};
                      const currentTemplate = currentSettings.template || {};
                      void setFieldValue(selectedTemplate, {
                        ...currentSettings,
                        template: {
                          ...currentTemplate,
                          [selectedLanguage]: e.target.value,
                        },
                      });
                    }}
                    disabled={isDisabled}
                    variant="outlined"
                    sx={{ direction: textDirection }}
                    error={(() => {
                      const templateErrors = errors[selectedTemplate as keyof typeof errors] as any;
                      const currentValue =
                        (values[selectedTemplate as keyof SMSTemplatesSettingsUpdateData] as any)?.template?.[
                          selectedLanguage
                        ] || '';
                      const hasFormikError =
                        templateErrors &&
                        touched[selectedTemplate as keyof typeof touched] &&
                        templateErrors[selectedLanguage];
                      const hasCharLimitError = calculateSMSLength(currentValue) > 130;
                      return Boolean(hasFormikError || hasCharLimitError);
                    })()}
                    helperText={(() => {
                      const templateErrors = errors[selectedTemplate as keyof typeof errors] as any;
                      if (
                        templateErrors &&
                        touched[selectedTemplate as keyof typeof touched] &&
                        templateErrors[selectedLanguage]
                      ) {
                        return templateErrors[selectedLanguage];
                      }
                      return undefined;
                    })()}
                  />

                  {renderCharacterCount(values)}

                  {orderPageIdOverride &&
                    !isTemplateInherited(selectedTemplate as keyof SMSTemplatesSettingsUpdateData) && (
                      <Box mt={2} sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            void setFieldValue(selectedTemplate, {});
                          }}>
                          {t('smsTemplates.resetToAccountLevel')}
                        </Button>
                      </Box>
                    )}

                  {/* Variable Helper */}
                  <Card sx={{ mt: 3, width: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('smsTemplates.availableVariables')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('smsTemplates.clickToInsert')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {TEMPLATE_VARIABLES(t).map((variable) => (
                          <Chip
                            key={variable.key}
                            label={variable.key}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const currentTemplate = (values[selectedTemplate]?.template as PartialLabelData) || {};
                              const currentValue = currentTemplate[selectedLanguage] || '';
                              const fieldName = `${selectedTemplate}.template.${selectedLanguage}`;
                              const newValue = currentValue + variable.key;
                              void setFieldValue(fieldName, newValue);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('smsTemplates.variableDescriptions')}
                        </Typography>
                        <Box
                          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
                          {TEMPLATE_VARIABLES(t).map((variable) => (
                            <Typography key={variable.key} variant="caption" display="block">
                              <strong>{variable.key}:</strong> {variable.descriptionKey}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Error Display */}
                  {submitError && (
                    <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mt: 2, width: '100%' }}>
                      <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {submitError}
                      </Typography>
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, width: '100%' }}>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      onClick={() => handleSubmit()}
                      disabled={isDisabled || isEqual(values, initialValues) || Object.keys(errors).length > 0}
                      loading={isSubmitting}
                      size="large">
                      {t('smsTemplates.saveTemplates')}
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
